// ui/shopModal.js
// Shop modal UI component with side-by-side buy/sell layout

import { gameState } from '../game/gameState.js';
import { SKILLS } from '../game/constants.js';
import { SHOP_ITEMS, findShopItem, AWAKENING_EMBER_COST } from '../data/shop/shopData.js';
import { getItemIcon, getItemColor, getItem, getItemSellPrice, canSellItem, getItemName } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';
import logger from '../utils/logger.js';
import { toastError, toastSuccess, toastWarning } from './toast.js';
import { isCorruptionActive } from '../systems/corruption.js';
import { buildEquipmentStatsTooltip } from './equipmentTooltip.js';
import { getTalentFlatBonus } from '../systems/talentTree/talentTree.js';
import { DORMANT_SET_ARMOR, AWAKENED_SET_ARMOR, SET_INFO, getAwakenedId } from '../data/items/setArmor.js';
import { SET_BONUSES } from '../data/items/setBonusData.js';

// Tier colors for equipment UI styling (border glow, name color)
// These are for tier indication, NOT material color matching
const TIER_COLORS = {
    tier1: '#CD853F',   // Tier 1 - peru/tan
    tier2: '#3CB371',   // Tier 2 - medium sea green
    tier3: '#5B9BD5',   // Tier 3 - royal blue
    tier4: '#E85D75',   // Tier 4 - crimson
    tier5: '#A8A8A8',   // Tier 5 - silver
    tier6: '#B088F9'    // Tier 6 - purple
};

/**
 * Get tier color based on combat level requirement
 */
function getTierColor(combatLevel) {
    if (!combatLevel || combatLevel < 15) return TIER_COLORS.tier1;
    if (combatLevel < 30) return TIER_COLORS.tier2;
    if (combatLevel < 45) return TIER_COLORS.tier3;
    if (combatLevel < 60) return TIER_COLORS.tier4;
    if (combatLevel < 75) return TIER_COLORS.tier5;
    return TIER_COLORS.tier6;
}

let modalElement = null;
let currentCategory = null;
let selectedQuantity = 1;
let quantityMode = 'fixed'; // 'fixed', 'all', or 'all-1'
let isAwakeningView = false;

// Set boss IDs for unlock check
const SET_BOSS_IDS = ['searing_matriarch', 'molten_colossus', 'cindered_knight'];

// Preset quantity buttons
const QUANTITY_PRESETS = [1, 5, 10, 50, 100];

// Category icons for sidebar
const CATEGORY_ICONS = {
    SKILLING: '🛠️',
    COMBAT_WEAPONS: '⚔️',
    COMBAT_ARMOR_MELEE: '🛡️',
    COMBAT_ARMOR_RANGED: '🏹',
    COMBAT_ARMOR_MAGIC: '🔮',
    QUEST_EQUIPMENT: '📜',
    RUNES: '✨',
    TOOLS: '🔧',
    AWAKENING: '🔥'
};

/**
 * Get category display name
 */
function getCategoryDisplayName(categoryKey) {
    const names = {
        SKILLING: 'Skilling',
        COMBAT_WEAPONS: 'Weapons',
        COMBAT_ARMOR_MELEE: 'Melee Armor',
        COMBAT_ARMOR_RANGED: 'Ranged Armor',
        COMBAT_ARMOR_MAGIC: 'Magic Armor',
        QUEST_EQUIPMENT: 'Quest Rewards',
        RUNES: 'Runes',
        TOOLS: 'Tools',
        AWAKENING: 'Awakening'
    };
    return names[categoryKey] || categoryKey.charAt(0) + categoryKey.slice(1).toLowerCase();
}

/**
 * Get available categories (non-empty ones)
 */
function getAvailableCategories() {
    return Object.entries(SHOP_ITEMS)
        .filter(([_, items]) => items.length > 0)
        .map(([key, items]) => ({ key, name: getCategoryDisplayName(key), icon: CATEGORY_ICONS[key] || '📦', items }));
}

/**
 * Render sidebar category list
 */
function renderCategorySidebar() {
    const categories = getAvailableCategories();
    if (categories.length === 0) return '';

    let html = categories.map(cat => `
        <button class="shop-category ${!isAwakeningView && currentCategory === cat.key ? 'active' : ''}"
                data-category="${cat.key}">
            <span class="shop-category-icon">${cat.icon}</span>
            <span class="shop-category-name">${cat.name}</span>
        </button>
    `).join('');

    // Add Awakening tab at the bottom with divider
    html += `
        <div class="shop-sidebar-divider"></div>
        <button class="shop-category ${isAwakeningView ? 'active awakening-tab' : 'awakening-tab'}"
                data-category="AWAKENING">
            <span class="shop-category-icon">${CATEGORY_ICONS.AWAKENING}</span>
            <span class="shop-category-name">Awakening</span>
        </button>
    `;

    return html;
}

/**
 * Build tooltip for shop item
 */
function buildShopItemTooltip(item, effectiveQty) {
    const itemData = getItem(item.grantItemId || item.id);
    const priceInfo = item.price > 0
        ? (effectiveQty > 1
            ? `${item.price.toLocaleString()} ${getItemIcon('coins')} each (${formatNumber(item.price * effectiveQty)} for ${effectiveQty})`
            : `${item.price.toLocaleString()} ${getItemIcon('coins')} each`)
        : 'Free';

    // Use equipment tooltip for equipment items
    if (itemData && itemData.slot) {
        return buildEquipmentStatsTooltip(item.id, true) + `\n\n${priceInfo}`;
    }

    // Non-equipment items
    let tooltip = item.name;
    if (itemData) {
        const stats = [];
        if (itemData.attackBonus) stats.push(`+${itemData.attackBonus} Attack`);
        if (itemData.defenseBonus) stats.push(`+${itemData.defenseBonus} Defense`);
        if (stats.length > 0) tooltip += `\n${stats.join(' | ')}`;
    }
    tooltip += `\n${priceInfo}`;
    return tooltip;
}

/**
 * Format time remaining until daily stock reset
 */
function formatTimeRemaining(resetTime) {
    const remaining = Math.max(0, resetTime - Date.now());
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

/**
 * Render a single shop item slot (buy panel)
 */
function renderBuySlot(item) {
    const displayItemId = item.grantItemId || item.id;
    const icon = getItemIcon(displayItemId);
    const color = getItemColor(displayItemId);
    const currentGold = gameState.getGold();
    const effectiveQty = getEffectiveBuyQuantity(item);
    const totalCost = item.price * effectiveQty;
    const canAfford = currentGold >= totalCost || item.price === 0;
    const corrupted = isCorruptionActive();

    // Check if this item requires a quest unlock
    const requiresUnlock = item.questUnlock;
    const hasUnlock = requiresUnlock ? gameState.hasQuestUnlock(item.questUnlock) : true;

    // Handle daily stock items
    let dailyStock = null;
    let dailyDepleted = false;
    if (item.dailyId && item.dailyStock) {
        dailyStock = gameState.getDailyShopStock(item.dailyId, item.dailyStock);
        dailyDepleted = dailyStock.remaining <= 0;
    }

    // Build classes
    let slotClass = '';
    if (!hasUnlock) {
        slotClass = 'quest-locked';
    } else if (dailyDepleted) {
        slotClass = 'daily-depleted';
    } else if (!canAfford && !corrupted) {
        slotClass = 'unaffordable';
    }

    // Build tooltip
    let tooltip = buildShopItemTooltip(item, effectiveQty);
    if (!hasUnlock) {
        const unlockName = gameState.getUnlockDisplayName(item.questUnlock);
        tooltip = `🔒 LOCKED\nComplete quest to unlock:\n${unlockName}\n\n${tooltip}`;
    }
    if (dailyStock) {
        tooltip += `\n${dailyStock.remaining}/${item.dailyStock} remaining`;
        if (dailyStock.resetTime > 0) {
            tooltip += `\nRestocks in ${formatTimeRemaining(dailyStock.resetTime)}`;
        }
    }

    // Price text
    let priceText;
    if (dailyStock) {
        if (dailyDepleted && dailyStock.resetTime > 0) {
            priceText = `${formatTimeRemaining(dailyStock.resetTime)}`;
        } else if (dailyDepleted) {
            priceText = 'Depleted';
        } else {
            priceText = `Free (${dailyStock.remaining})`;
        }
    } else if (corrupted && hasUnlock) {
        priceText = 'FREE';
    } else if (effectiveQty > 1) {
        priceText = `${formatNumber(totalCost)}`;
    } else {
        priceText = `${formatNumber(item.price)}`;
    }

    const priceIcon = dailyStock ? '' : ` ${getItemIcon('coins', effectiveQty > 1 ? totalCost : item.price)}`;

    return `
        <div class="shop-slot ${slotClass}"
             data-item-id="${item.id}"
             data-action="buy"
             data-tooltip="${tooltip.replace(/"/g, '&quot;')}">
            <div class="shop-slot-content" style="background-color: ${hasUnlock ? color : '#333'};">
                <span class="shop-slot-icon">${hasUnlock ? icon : '🔒'}</span>
            </div>
            <span class="shop-slot-price${dailyStock && !dailyDepleted ? ' daily-free' : ''}">${priceText}${priceIcon}</span>
        </div>
    `;
}

/**
 * Render items grid for current category (buy panel)
 */
function renderBuyGrid() {
    if (!currentCategory || !SHOP_ITEMS[currentCategory]) {
        return '<div class="shop-empty">Select a category</div>';
    }

    const items = SHOP_ITEMS[currentCategory];
    if (items.length === 0) {
        return '<div class="shop-empty">No items in this category</div>';
    }

    return items.map(item => renderBuySlot(item)).join('');
}

/**
 * Build tooltip for inventory item (sell panel)
 */
function buildSellItemTooltip(itemId, quantity) {
    const item = getItem(itemId);
    const sellable = canSellItem(itemId);
    const baseSellPrice = getItemSellPrice(itemId);
    const shopSellBonus = getTalentFlatBonus(SKILLS.PILFERING, 'shopSellBonus') || 0;
    const sellPrice = Math.floor(baseSellPrice * (1 + shopSellBonus));
    const effectiveSellQty = getEffectiveSellQuantity(quantity);
    const totalSellValue = sellPrice * effectiveSellQty;

    const sellInfo = sellable
        ? (effectiveSellQty > 1
            ? `Sell ${effectiveSellQty} for ${totalSellValue.toLocaleString()} ${getItemIcon('coins')} (${sellPrice.toLocaleString()} each)`
            : `Sell for ${sellPrice.toLocaleString()} ${getItemIcon('coins')} each`)
        : 'Cannot sell';

    // Use equipment tooltip for equipment items
    if (item && item.slot) {
        let tooltip = buildEquipmentStatsTooltip(itemId, false);
        tooltip += `\nOwned: ${quantity.toLocaleString()}`;
        tooltip += `\n${sellInfo}`;
        return tooltip;
    }

    // Non-equipment items
    let tooltip = `${getItemName(itemId)} (${quantity.toLocaleString()})`;
    tooltip += `\n${sellInfo}`;
    return tooltip;
}

/**
 * Render a single inventory slot (sell panel)
 */
function renderSellSlot(slot, index) {
    if (!slot) {
        return `<div class="shop-slot empty" data-slot="${index}"></div>`;
    }

    const { itemId, quantity } = slot;
    const sellable = canSellItem(itemId);
    const baseSellPrice = getItemSellPrice(itemId);
    const shopSellBonus = getTalentFlatBonus(SKILLS.PILFERING, 'shopSellBonus') || 0;
    const sellPrice = Math.floor(baseSellPrice * (1 + shopSellBonus));
    const icon = getItemIcon(itemId, quantity);
    const color = getItemColor(itemId);
    const formattedQty = formatNumber(quantity);
    const effectiveSellQty = getEffectiveSellQuantity(quantity);
    const sellValue = sellPrice * effectiveSellQty;

    const isLocked = gameState.isItemLocked(itemId);
    const lockedClass = isLocked ? ' locked' : '';
    const lockIcon = isLocked ? '<span class="lock-icon">🔒</span>' : '';
    const sellableClass = sellable ? 'sellable' : 'not-sellable';
    const tooltip = buildSellItemTooltip(itemId, quantity);

    return `
        <div class="shop-slot ${sellableClass}${lockedClass}"
             data-item-id="${itemId}"
             data-slot="${index}"
             data-action="sell"
             data-tooltip="${tooltip.replace(/"/g, '&quot;')}">
            <div class="shop-slot-content" style="background-color: ${color};">
                <span class="shop-slot-icon">${icon}</span>
            </div>
            ${lockIcon}
            <span class="shop-slot-qty">${formattedQty}</span>
            ${sellable && sellValue > 0 ? `<span class="shop-slot-price sell-price">+${formatNumber(sellValue)} ${getItemIcon('coins', sellValue)}</span>` : ''}
        </div>
    `;
}

/**
 * Render inventory grid (sell panel)
 */
function renderSellGrid() {
    const slots = gameState.getInventorySlots();
    return slots.map((slot, index) => renderSellSlot(slot, index)).join('');
}

/**
 * Render quantity selector
 */
function renderQuantitySelector() {
    const presetButtons = QUANTITY_PRESETS.map(qty => `
        <button class="shop-qty-btn ${quantityMode === 'fixed' && selectedQuantity === qty ? 'active' : ''}"
                data-quantity="${qty}">
            ${qty}
        </button>
    `).join('');

    return `
        <div class="shop-quantity-selector">
            <span class="shop-qty-label">Qty:</span>
            <div class="shop-qty-presets">
                ${presetButtons}
                <button class="shop-qty-btn shop-qty-special ${quantityMode === 'all' ? 'active' : ''}"
                        data-qty-mode="all">All</button>
                <button class="shop-qty-btn shop-qty-special ${quantityMode === 'all-1' ? 'active' : ''}"
                        data-qty-mode="all-1">All-1</button>
            </div>
            <div class="shop-qty-custom">
                <input type="number"
                       class="shop-qty-input"
                       min="1"
                       value="${quantityMode === 'fixed' ? selectedQuantity : ''}"
                       placeholder="X">
                <button class="shop-qty-set-btn">Set</button>
            </div>
        </div>
    `;
}

// ==================== AWAKENING STORE ====================

/**
 * Check if the awakening store is unlocked (any set boss defeated)
 */
function isAwakeningUnlocked() {
    return SET_BOSS_IDS.some(id => gameState.hasDefeatedBoss(id));
}

/**
 * Get piece status for a specific set piece slot
 * Returns: 'unowned' | 'dormant' | 'awakened'
 */
function getPieceStatus(setId, slotId) {
    const awakenedId = `awakened_${setId}_${slotId}`;
    const dormantId = `dormant_${setId}_${slotId}`;

    // Check inventory + equipment for awakened
    if (gameState.getItemCount(awakenedId) > 0 || isEquipped(awakenedId)) return 'awakened';
    // Check inventory + equipment for dormant
    if (gameState.getItemCount(dormantId) > 0 || isEquipped(dormantId)) return 'dormant';
    return 'unowned';
}

/**
 * Check if an item is equipped in any equipment set
 */
function isEquipped(itemId) {
    const sets = gameState.getEquipmentSets();
    for (const setId of ['melee', 'ranged', 'magic']) {
        const gear = sets[setId]?.gear;
        if (gear) {
            for (const slotItemId of Object.values(gear)) {
                if (slotItemId === itemId) return true;
            }
        }
    }
    return false;
}

/**
 * Find where an item is equipped: returns { setId, slot } or null
 */
function findEquippedLocation(itemId) {
    const sets = gameState.getEquipmentSets();
    for (const setId of ['melee', 'ranged', 'magic']) {
        const gear = sets[setId]?.gear;
        if (gear) {
            for (const [slot, slotItemId] of Object.entries(gear)) {
                if (slotItemId === itemId) return { setId, slot };
            }
        }
    }
    return null;
}

/**
 * Count awakened pieces for a set
 */
function countAwakenedPieces(setId) {
    const slotIds = getSetSlotIds(setId);
    return slotIds.filter(slotId => getPieceStatus(setId, slotId) === 'awakened').length;
}

/**
 * Get slot IDs for a set (the craftSlot IDs used in item IDs)
 */
function getSetSlotIds(setId) {
    const setConfig = [
        { setId: 'ashforged', slots: ['head', 'chestplate', 'legguards', 'boots', 'gloves', 'shoulders'] },
        { setId: 'cindermark', slots: ['head', 'chestplate', 'legguards', 'boots', 'gloves', 'shoulders'] },
        { setId: 'pyreweave', slots: ['head', 'chestplate', 'legguards', 'boots', 'gloves', 'shoulders'] }
    ];
    const config = setConfig.find(c => c.setId === setId);
    return config ? config.slots : [];
}

/**
 * Get display name for a slot ID
 */
function getSlotDisplayName(slotId) {
    const names = {
        head: 'Head',
        chestplate: 'Body',
        legguards: 'Legs',
        boots: 'Feet',
        gloves: 'Hands',
        shoulders: 'Shoulders'
    };
    return names[slotId] || slotId;
}

/**
 * Render the awakening store locked message
 */
function renderAwakeningLocked() {
    return `
        <div class="awakening-locked">
            <div class="awakening-locked-icon">🔒</div>
            <div class="awakening-locked-title">Sealed by Ancient Fire</div>
            <div class="awakening-locked-text">
                This area seems sealed... Exploring deeper into the Veil may give us answers.
            </div>
            <div class="awakening-locked-hint">
                Defeat a Set Boss in The Ashen Crucible (Tier 2) to unlock the Awakening Store.
            </div>
        </div>
    `;
}

/**
 * Render a single set panel with its 6 pieces and bonus preview
 */
function renderSetPanel(setId) {
    const setInfo = SET_INFO[setId];
    const setBonusData = SET_BONUSES[setId];
    if (!setInfo || !setBonusData) return '';

    const slotIds = getSetSlotIds(setId);
    const awakenedCount = countAwakenedPieces(setId);
    const emberCount = gameState.getItemCount('veil_ember');

    // Render pieces grid
    const piecesHTML = slotIds.map(slotId => {
        const status = getPieceStatus(setId, slotId);
        const dormantId = `dormant_${setId}_${slotId}`;
        const awakenedId = `awakened_${setId}_${slotId}`;
        const dormantItem = getItem(dormantId);
        const awakenedItem = getItem(awakenedId);
        const displayItem = status === 'awakened' ? awakenedItem : dormantItem;
        const displayName = displayItem ? displayItem.name : `${setInfo.name} ${getSlotDisplayName(slotId)}`;

        let statusIcon = '';
        let statusClass = '';
        let statusLabel = '';
        let canAwaken = false;

        if (status === 'awakened') {
            statusIcon = '✓';
            statusClass = 'piece-awakened';
            statusLabel = 'Awakened';
        } else if (status === 'dormant') {
            const hasEmbers = emberCount >= AWAKENING_EMBER_COST;
            canAwaken = hasEmbers;
            statusIcon = '🔥';
            statusClass = hasEmbers ? 'piece-ready' : 'piece-dormant';
            statusLabel = hasEmbers ? 'Ready to Awaken' : `Need ${AWAKENING_EMBER_COST} Embers`;
        } else {
            statusIcon = '?';
            statusClass = 'piece-unowned';
            statusLabel = 'Not obtained';
        }

        const icon = status !== 'unowned' && displayItem ? getItemIcon(displayItem.id) : '';
        const color = status !== 'unowned' && displayItem ? getItemColor(displayItem.id) : '#333';

        return `
            <div class="awakening-piece ${statusClass}" data-dormant-id="${dormantId}" data-set-id="${setId}" data-can-awaken="${canAwaken}">
                <div class="awakening-piece-icon" style="background-color: ${color};">
                    ${status === 'unowned' ? '<span class="piece-silhouette">?</span>' : `<span class="piece-item-icon">${icon}</span>`}
                    <span class="piece-status-badge ${statusClass}">${statusIcon}</span>
                </div>
                <div class="awakening-piece-info">
                    <div class="awakening-piece-name${status === 'unowned' ? ' unknown' : ''}">${status === 'unowned' ? getSlotDisplayName(slotId) : displayName}</div>
                    <div class="awakening-piece-status">${statusLabel}</div>
                </div>
                ${status === 'dormant' && canAwaken ? `<button class="awakening-btn" data-action="awaken" data-dormant-id="${dormantId}">Awaken</button>` : ''}
            </div>
        `;
    }).join('');

    // Render set bonus preview
    const bonusesHTML = [2, 4, 6].map(threshold => {
        const bonus = setBonusData.bonuses[threshold];
        const isActive = awakenedCount >= threshold;
        return `
            <div class="set-bonus-row ${isActive ? 'bonus-active' : 'bonus-inactive'}">
                <span class="set-bonus-threshold">(${threshold})</span>
                <span class="set-bonus-name">${bonus.name}</span>
                <span class="set-bonus-desc">${bonus.description}</span>
            </div>
        `;
    }).join('');

    return `
        <div class="awakening-set-panel" style="border-color: ${setInfo.color};">
            <div class="awakening-set-header" style="background: linear-gradient(135deg, ${setInfo.color}33, ${setInfo.color}11);">
                <div class="awakening-set-title">
                    <span class="set-name" style="color: ${setInfo.color};">${setInfo.name}</span>
                    <span class="set-type">${setInfo.armorType.charAt(0).toUpperCase() + setInfo.armorType.slice(1)} Set</span>
                </div>
                <div class="awakening-set-count">
                    <span class="piece-count ${awakenedCount >= 6 ? 'complete' : ''}">${awakenedCount}/6</span> awakened
                </div>
            </div>
            <div class="awakening-set-pieces">
                ${piecesHTML}
            </div>
            <div class="awakening-set-bonuses">
                ${bonusesHTML}
            </div>
        </div>
    `;
}

/**
 * Render the full awakening store content
 */
function renderAwakeningContent() {
    if (!isAwakeningUnlocked()) {
        return renderAwakeningLocked();
    }

    const emberCount = gameState.getItemCount('veil_ember');
    const tokenCount = gameState.getItemCount('universal_set_token');

    let html = `
        <div class="awakening-header">
            <div class="awakening-title">Awakening Store</div>
            <div class="awakening-resources">
                <span class="ember-counter" data-tooltip="Veil Embers — used to awaken dormant set armor. ${AWAKENING_EMBER_COST} per piece.">
                    🔥 ${formatNumber(emberCount)} Veil Embers
                </span>
                ${tokenCount > 0 ? `
                    <button class="token-redeem-btn" data-action="redeem-token" data-tooltip="Redeem a Universal Set Token for any dormant set armor piece.">
                        🎫 Redeem Token (${tokenCount})
                    </button>
                ` : ''}
            </div>
        </div>
        <div class="awakening-sets">
    `;

    // Render all 3 sets
    for (const setId of ['ashforged', 'cindermark', 'pyreweave']) {
        html += renderSetPanel(setId);
    }

    html += '</div>';
    return html;
}

/**
 * Awaken a dormant piece: consume dormant + embers, produce awakened
 */
function awakenPiece(dormantItemId) {
    const emberCount = gameState.getItemCount('veil_ember');
    if (emberCount < AWAKENING_EMBER_COST) {
        toastError(`Need ${AWAKENING_EMBER_COST} Veil Embers (have ${emberCount})`);
        return;
    }

    const dormantCount = gameState.getItemCount(dormantItemId);
    const equippedLocation = findEquippedLocation(dormantItemId);
    if (dormantCount <= 0 && !equippedLocation) {
        toastError('Dormant piece not found');
        return;
    }

    const awakenedId = getAwakenedId(dormantItemId);
    const awakenedItem = getItem(awakenedId);
    if (!awakenedItem) {
        toastError('Awakened version not found');
        return;
    }

    // If the piece is equipped (and not also in inventory), swap directly in the gear slot
    if (equippedLocation && dormantCount <= 0) {
        const sets = gameState.getEquipmentSets();
        const gear = sets[equippedLocation.setId].gear;

        // Consume embers
        gameState.removeItem('veil_ember', AWAKENING_EMBER_COST);

        // Directly swap the gear slot from dormant to awakened (no inventory needed)
        gear[equippedLocation.slot] = awakenedId;

        const itemName = getItemName(awakenedId);
        toastSuccess(`Awakened: ${itemName}!`);
        logger.debug(`[AWAKENING] ${dormantItemId} → ${awakenedId} (swapped in ${equippedLocation.setId}/${equippedLocation.slot})`);
        updateDisplay();
        return;
    }

    // Standard path: piece is in inventory
    gameState.removeItem(dormantItemId, 1);
    gameState.removeItem('veil_ember', AWAKENING_EMBER_COST);

    const result = gameState.addItem(awakenedId, 1);
    if (!result.success) {
        // Refund
        gameState.addItem(dormantItemId, 1);
        gameState.addItem('veil_ember', AWAKENING_EMBER_COST);
        toastError('Inventory full — cannot awaken');
        return;
    }

    const itemName = getItemName(awakenedId);
    toastSuccess(`Awakened: ${itemName}!`);
    logger.debug(`[AWAKENING] ${dormantItemId} → ${awakenedId}`);
    updateDisplay();
}

/**
 * Render the token redemption modal overlay
 */
function renderTokenRedemptionModal() {
    const tokenCount = gameState.getItemCount('universal_set_token');
    if (tokenCount <= 0) return '';

    let html = `
        <div class="token-modal-overlay" id="token-modal-overlay">
            <div class="token-modal">
                <div class="token-modal-header">
                    <h3>🎫 Redeem Universal Set Token</h3>
                    <button class="modal-close" data-action="close-token-modal">&times;</button>
                </div>
                <div class="token-modal-body">
                    <p class="token-modal-desc">Choose a dormant set armor piece to receive:</p>
                    <div class="token-sets">
    `;

    for (const setId of ['ashforged', 'cindermark', 'pyreweave']) {
        const setInfo = SET_INFO[setId];
        const slotIds = getSetSlotIds(setId);

        html += `
            <div class="token-set-section">
                <div class="token-set-name" style="color: ${setInfo.color};">${setInfo.name} (${setInfo.armorType})</div>
                <div class="token-pieces-list">
        `;

        for (const slotId of slotIds) {
            const dormantId = `dormant_${setId}_${slotId}`;
            const dormantItem = getItem(dormantId);
            if (!dormantItem) continue;

            const status = getPieceStatus(setId, slotId);
            const alreadyOwned = status !== 'unowned';

            html += `
                <button class="token-piece-btn ${alreadyOwned ? 'already-owned' : ''}"
                        data-action="confirm-token" data-dormant-id="${dormantId}"
                        ${alreadyOwned ? 'data-tooltip="Already owned"' : ''}>
                    <span class="token-piece-icon">${getItemIcon(dormantId)}</span>
                    <span class="token-piece-name">${dormantItem.name}</span>
                    <span class="token-piece-slot">${getSlotDisplayName(slotId)}</span>
                    ${alreadyOwned ? '<span class="token-owned-badge">Owned</span>' : ''}
                </button>
            `;
        }

        html += '</div></div>';
    }

    html += '</div></div></div>';
    return html;
}

/**
 * Open the token redemption modal
 */
function openTokenModal() {
    const tokenCount = gameState.getItemCount('universal_set_token');
    if (tokenCount <= 0) {
        toastError('No Universal Set Tokens available');
        return;
    }

    const existingOverlay = document.getElementById('token-modal-overlay');
    if (existingOverlay) existingOverlay.remove();

    const container = document.createElement('div');
    container.innerHTML = renderTokenRedemptionModal();
    const overlay = container.firstElementChild;
    if (overlay) {
        document.body.appendChild(overlay);
        overlay.addEventListener('click', handleTokenModalClick);
    }
}

/**
 * Close the token redemption modal
 */
function closeTokenModal() {
    const overlay = document.getElementById('token-modal-overlay');
    if (overlay) {
        overlay.remove();
    }
}

/**
 * Handle clicks inside the token modal
 */
function handleTokenModalClick(e) {
    // Close button
    if (e.target.closest('[data-action="close-token-modal"]')) {
        closeTokenModal();
        return;
    }

    // Confirm token redemption
    const confirmBtn = e.target.closest('[data-action="confirm-token"]');
    if (confirmBtn) {
        const dormantId = confirmBtn.dataset.dormantId;
        redeemToken(dormantId);
        return;
    }

    // Close on overlay click
    if (e.target.id === 'token-modal-overlay') {
        closeTokenModal();
    }
}

/**
 * Redeem a Universal Set Token for a specific dormant piece
 */
function redeemToken(dormantItemId) {
    const tokenCount = gameState.getItemCount('universal_set_token');
    if (tokenCount <= 0) {
        toastError('No Universal Set Tokens available');
        return;
    }

    const dormantItem = getItem(dormantItemId);
    if (!dormantItem) {
        toastError('Invalid item');
        return;
    }

    // Consume token
    gameState.removeItem('universal_set_token', 1);

    // Grant dormant piece
    const result = gameState.addItem(dormantItemId, 1);
    if (!result.success) {
        // Refund token
        gameState.addItem('universal_set_token', 1);
        toastError('Inventory full — cannot redeem token');
        return;
    }

    toastSuccess(`Redeemed token: ${dormantItem.name}!`);
    logger.debug(`[AWAKENING] Token redeemed for ${dormantItemId}`);
    closeTokenModal();
    updateDisplay();
}

/**
 * Render the modal body content (shop or awakening)
 */
function renderModalBody() {
    if (isAwakeningView) {
        return `
            <div class="shop-split-panels">
                <div class="shop-buy-panel">
                    <div class="shop-sidebar">
                        <div class="shop-sidebar-header">🛒 Buy</div>
                        ${renderCategorySidebar()}
                    </div>
                    <div class="shop-buy-content awakening-content">
                        ${renderAwakeningContent()}
                    </div>
                </div>
            </div>
        `;
    }

    const usedSlots = gameState.getUniqueItemCount();
    return `
        <div class="shop-split-panels">
            <!-- Left: Category sidebar + Buy grid -->
            <div class="shop-buy-panel">
                <div class="shop-sidebar">
                    <div class="shop-sidebar-header">🛒 Buy</div>
                    ${renderCategorySidebar()}
                </div>
                <div class="shop-buy-content">
                    <div class="shop-buy-grid">
                        ${renderBuyGrid()}
                    </div>
                </div>
            </div>

            <!-- Right: Sell/Inventory panel -->
            <div class="shop-sell-panel">
                <div class="shop-panel-header">
                    <span class="shop-panel-title">${getItemIcon('coins')} Sell</span>
                    <span class="shop-panel-subtitle">Inventory</span>
                    <span class="shop-panel-count">${usedSlots}/28</span>
                </div>
                <div class="shop-sell-grid">
                    ${renderSellGrid()}
                </div>
            </div>
        </div>
    `;
}

/**
 * Create the shop modal HTML - side-by-side layout
 */
function createModalHTML() {
    const currentGold = gameState.getGold();
    const categories = getAvailableCategories();

    // Set initial category if not set
    if (!isAwakeningView && !currentCategory && categories.length > 0) {
        currentCategory = categories[0].key;
    }

    return `
        <div class="modal-overlay" id="shop-modal-overlay">
            <div class="modal-content shop-modal">
                <div class="modal-header">
                    <h2>${isAwakeningView ? 'Awakening Store' : 'Shop'}</h2>
                    <div class="shop-gold-display">
                        <span class="gold-icon">${getItemIcon('coins', currentGold)}</span>
                        <span class="gold-amount">Gold: ${currentGold.toLocaleString()}</span>
                    </div>
                    <button class="modal-close" onclick="window.shopModal.close()">&times;</button>
                </div>
                <div class="modal-body">
                    ${!isAwakeningView ? `
                    <div class="shop-controls-bar">
                        ${renderQuantitySelector()}
                    </div>
                    ` : ''}
                    ${renderModalBody()}
                    ${!isAwakeningView ? `
                    <div class="shop-footer">
                        <span class="shop-hint">Click to buy • Click inventory to sell • Right-click to lock</span>
                    </div>
                    ` : ''}
                </div>
            </div>
        </div>
    `;
}

/**
 * Update the buy grid display
 */
function updateBuyGrid() {
    if (!modalElement) return;

    const buyGrid = modalElement.querySelector('.shop-buy-grid');
    if (buyGrid) {
        buyGrid.innerHTML = renderBuyGrid();
    }
}

/**
 * Update the sell grid display
 */
function updateSellGrid() {
    if (!modalElement) return;

    const sellGrid = modalElement.querySelector('.shop-sell-grid');
    if (sellGrid) {
        sellGrid.innerHTML = renderSellGrid();
    }

    // Update inventory count
    const countEl = modalElement.querySelector('.shop-panel-count');
    if (countEl) {
        countEl.textContent = `${gameState.getUniqueItemCount()}/28`;
    }
}

/**
 * Update gold display
 */
function updateGoldDisplay() {
    if (!modalElement) return;

    const currentGold = gameState.getGold();
    const goldAmount = modalElement.querySelector('.gold-amount');
    if (goldAmount) {
        goldAmount.textContent = `Gold: ${currentGold.toLocaleString()}`;
    }
    const goldIcon = modalElement.querySelector('.gold-icon');
    if (goldIcon) {
        goldIcon.innerHTML = getItemIcon('coins', currentGold);
    }
}

/**
 * Update all displays
 */
function updateDisplay() {
    updateGoldDisplay();

    if (isAwakeningView) {
        // Re-render awakening content
        const awakeningEl = modalElement?.querySelector('.awakening-content');
        if (awakeningEl) {
            awakeningEl.innerHTML = renderAwakeningContent();
        }
    } else {
        updateBuyGrid();
        updateSellGrid();
        updateQuantityButtons();
    }
}

/**
 * Update quantity preset buttons
 */
function updateQuantityButtons() {
    if (!modalElement) return;

    modalElement.querySelectorAll('.shop-qty-btn').forEach(btn => {
        if (btn.dataset.qtyMode) {
            btn.classList.toggle('active', quantityMode === btn.dataset.qtyMode);
        } else {
            const qty = parseInt(btn.dataset.quantity);
            btn.classList.toggle('active', quantityMode === 'fixed' && qty === selectedQuantity);
        }
    });
}

/**
 * Switch to a category
 */
function switchCategory(categoryKey) {
    if (categoryKey === 'AWAKENING') {
        isAwakeningView = true;
        currentCategory = null;
    } else {
        isAwakeningView = false;
        currentCategory = categoryKey;
    }

    if (!modalElement) return;

    // Re-render the whole modal body to switch between shop/awakening views
    const modalBody = modalElement.querySelector('.modal-body');
    if (modalBody) {
        modalBody.innerHTML = renderModalBody();
    }

    // Re-render the sidebar to update active state
    const sidebar = modalElement.querySelector('.shop-sidebar');
    if (sidebar) {
        const sidebarHeader = sidebar.querySelector('.shop-sidebar-header');
        const headerHTML = sidebarHeader ? sidebarHeader.outerHTML : '';
        sidebar.innerHTML = headerHTML + renderCategorySidebar();
    }

    // Update controls bar and footer visibility
    const controlsBar = modalElement.querySelector('.shop-controls-bar');
    const footer = modalElement.querySelector('.shop-footer');
    if (isAwakeningView) {
        if (controlsBar) controlsBar.style.display = 'none';
        if (footer) footer.style.display = 'none';
    } else {
        if (footer) footer.style.display = '';
        if (controlsBar) {
            controlsBar.style.display = '';
        } else {
            // Re-insert controls bar if switching back from awakening
            const mBody = modalElement.querySelector('.modal-body');
            const splitPanels = mBody?.querySelector('.shop-split-panels');
            if (mBody && splitPanels) {
                const controlsHTML = `
                    <div class="shop-controls-bar">
                        ${renderQuantitySelector()}
                    </div>
                `;
                splitPanels.insertAdjacentHTML('beforebegin', controlsHTML);
                // Re-attach input listener
                const input = modalElement.querySelector('.shop-qty-input');
                if (input) input.addEventListener('keypress', handleCustomInputKeypress);
            }
        }
        if (!footer) {
            const mBody = modalElement.querySelector('.modal-body');
            if (mBody) {
                mBody.insertAdjacentHTML('beforeend', `
                    <div class="shop-footer">
                        <span class="shop-hint">Click to buy • Click inventory to sell • Right-click to lock</span>
                    </div>
                `);
            }
        }
    }
}

/**
 * Set quantity from preset or custom
 */
function setQuantity(qty) {
    selectedQuantity = Math.max(1, parseInt(qty) || 1);
    quantityMode = 'fixed';
    updateDisplay();
}

function setQuantityMode(mode) {
    quantityMode = mode;
    updateDisplay();
}

/**
 * Get effective buy quantity for an item based on current mode
 */
function getEffectiveBuyQuantity(shopItem) {
    if (quantityMode === 'all') {
        if (shopItem.price <= 0) return shopItem.dailyStock || 100;
        const gold = gameState.getGold();
        return Math.max(1, Math.floor(gold / shopItem.price));
    }
    if (quantityMode === 'all-1') {
        // All-1 doesn't make much sense for buying, treat as All
        if (shopItem.price <= 0) return shopItem.dailyStock || 100;
        const gold = gameState.getGold();
        return Math.max(1, Math.floor(gold / shopItem.price));
    }
    return selectedQuantity;
}

/**
 * Get effective sell quantity for an item based on current mode
 */
function getEffectiveSellQuantity(currentCount) {
    if (quantityMode === 'all') {
        return currentCount;
    }
    if (quantityMode === 'all-1') {
        return Math.max(0, currentCount - 1);
    }
    return Math.min(currentCount, selectedQuantity);
}

/**
 * Sell an item
 */
export function sellItem(itemId, quantity) {
    const item = getItem(itemId);
    if (!item) {
        toastError('Item not found');
        return { success: false, reason: 'Item not found' };
    }

    if (!canSellItem(itemId)) {
        toastError('This item cannot be sold');
        return { success: false, reason: 'Item not sellable' };
    }

    if (gameState.isItemLocked(itemId)) {
        toastWarning('Item is locked! Right-click to unlock.');
        return { success: false, reason: 'Item locked' };
    }

    const currentCount = gameState.getItemCount(itemId);
    // Calculate effective quantity based on mode
    if (quantity === undefined) {
        quantity = getEffectiveSellQuantity(currentCount);
    }
    const sellQty = Math.min(currentCount, quantity);

    if (sellQty <= 0) {
        toastError('No items to sell');
        return { success: false, reason: 'No items to sell' };
    }

    const sellPrice = getItemSellPrice(itemId);
    // Apply Fence Connections talent (+15% shop sell prices)
    const shopSellBonus = getTalentFlatBonus(SKILLS.PILFERING, 'shopSellBonus') || 0;
    const totalValue = Math.floor(sellPrice * sellQty * (1 + shopSellBonus));

    // Remove the items
    const removed = gameState.removeItem(itemId, sellQty);
    if (!removed) {
        toastError('Failed to remove items');
        return { success: false, reason: 'Failed to remove items' };
    }

    // Add the gold (bypass addGold to avoid double-applying Coin Magnet)
    gameState.addItem('coins', totalValue);

    const itemName = getItemName(itemId);
    logger.debug(`[SHOP] Sold ${sellQty}x ${itemName} for ${formatNumber(totalValue)} gold`);
    toastSuccess(`Sold ${sellQty}x ${itemName} for ${formatNumber(totalValue)} gold`);

    // Update the display
    updateDisplay();

    return { success: true, sold: sellQty, value: totalValue };
}

/**
 * Purchase an item
 */
export function purchaseItem(itemId, quantity) {
    const shopItem = findShopItem(itemId);
    if (!shopItem) {
        logger.warn(`[SHOP] Item not found: ${itemId}`);
        toastError('Item not found');
        return { success: false, reason: 'Item not found' };
    }

    // Calculate effective quantity based on mode
    if (quantity === undefined) {
        quantity = getEffectiveBuyQuantity(shopItem);
    }

    // Check if this item requires a quest unlock
    if (shopItem.questUnlock && !gameState.hasQuestUnlock(shopItem.questUnlock)) {
        const unlockName = gameState.getUnlockDisplayName(shopItem.questUnlock);
        toastError(`Locked! Complete quest for: ${unlockName}`);
        return { success: false, reason: 'Quest unlock required' };
    }

    const corrupted = isCorruptionActive();

    // Handle daily stock items
    if (shopItem.dailyId && shopItem.dailyStock) {
        const stock = gameState.getDailyShopStock(shopItem.dailyId, shopItem.dailyStock);
        if (stock.remaining <= 0) {
            toastError(`Daily stock depleted! Restocks in ${formatTimeRemaining(stock.resetTime)}`);
            return { success: false, reason: 'Daily stock depleted' };
        }
        // Cap quantity at remaining stock
        const actualQty = Math.min(quantity, stock.remaining);
        const actualItemId = shopItem.grantItemId || shopItem.id;

        const result = gameState.addItem(actualItemId, actualQty);
        if (!result.success) {
            toastError('Inventory full');
            return { success: false, reason: result.reason || 'Inventory full' };
        }

        gameState.consumeDailyShopStock(shopItem.dailyId, actualQty);
        const remaining = stock.remaining - actualQty;
        logger.debug(`[SHOP] Claimed ${actualQty}x ${shopItem.name} (${remaining} remaining)`);
        toastSuccess(`Claimed ${actualQty}x free ${getItemName(actualItemId)} (${remaining} left today)`);
        updateDisplay();
        return { success: true, purchased: actualQty, cost: 0 };
    }

    const totalCost = shopItem.price * quantity;
    const currentGold = gameState.getGold();

    // Skip gold check if corruption is active
    if (!corrupted && currentGold < totalCost) {
        const needed = totalCost - currentGold;
        toastError(`Not enough gold (need ${formatNumber(needed)} more)`);
        return { success: false, reason: 'Not enough gold' };
    }

    // Deduct gold (skip if corruption is active)
    if (!corrupted) {
        const goldRemoved = gameState.removeItem('coins', totalCost);
        if (!goldRemoved) {
            toastError('Failed to deduct gold');
            return { success: false, reason: 'Failed to deduct gold' };
        }
    }

    // Add the item (use grantItemId if different from shop entry id)
    const actualItemId = shopItem.grantItemId || itemId;
    const result = gameState.addItem(actualItemId, quantity);

    if (!result.success) {
        // Refund gold if inventory full
        if (!corrupted) {
            gameState.addItem('coins', totalCost);
        }
        toastError('Inventory full');
        return { success: false, reason: result.reason || 'Inventory full' };
    }

    const costMsg = corrupted ? ' (FREE - Corrupted!)' : ` for ${formatNumber(totalCost)} gold`;
    logger.debug(`[SHOP] Purchased ${quantity}x ${shopItem.name}${costMsg}`);
    toastSuccess(`Bought ${quantity}x ${shopItem.name}${corrupted ? ' FREE' : ''}`);

    // Update the display
    updateDisplay();

    return { success: true, purchased: quantity, cost: corrupted ? 0 : totalCost };
}

/**
 * Handle clicks within the modal
 */
function handleModalClick(e) {
    // Category sidebar click
    const categoryBtn = e.target.closest('.shop-category');
    if (categoryBtn) {
        switchCategory(categoryBtn.dataset.category);
        return;
    }

    // Awakening actions
    const awakenBtn = e.target.closest('[data-action="awaken"]');
    if (awakenBtn) {
        awakenPiece(awakenBtn.dataset.dormantId);
        return;
    }

    const redeemTokenBtn = e.target.closest('[data-action="redeem-token"]');
    if (redeemTokenBtn) {
        openTokenModal();
        return;
    }

    // Quantity mode button click (All, All-1)
    const modeBtn = e.target.closest('[data-qty-mode]');
    if (modeBtn) {
        setQuantityMode(modeBtn.dataset.qtyMode);
        return;
    }

    // Quantity preset click
    const qtyBtn = e.target.closest('.shop-qty-btn:not([data-qty-mode])');
    if (qtyBtn && qtyBtn.dataset.quantity) {
        setQuantity(parseInt(qtyBtn.dataset.quantity));
        return;
    }

    // Custom quantity set button
    const setBtn = e.target.closest('.shop-qty-set-btn');
    if (setBtn) {
        const input = modalElement.querySelector('.shop-qty-input');
        if (input) {
            setQuantity(parseInt(input.value) || 1);
        }
        return;
    }

    // Shop slot click (buy or sell)
    const shopSlot = e.target.closest('.shop-slot');
    if (shopSlot && !shopSlot.classList.contains('empty')) {
        const itemId = shopSlot.dataset.itemId;
        const action = shopSlot.dataset.action;

        if (action === 'sell') {
            if (shopSlot.classList.contains('sellable')) {
                sellItem(itemId);
            }
        } else if (action === 'buy') {
            if (!shopSlot.classList.contains('unaffordable') && !shopSlot.classList.contains('quest-locked')) {
                purchaseItem(itemId);
            }
        }
        return;
    }

    // Close on overlay click
    if (e.target === modalElement) {
        closeShopModal();
    }
}

/**
 * Handle right-click on sell slots to toggle item lock
 */
function handleShopContextMenu(e) {
    const slot = e.target.closest('.shop-slot[data-action="sell"]');
    if (!slot) return;
    const itemId = slot.dataset.itemId;
    if (!itemId) return;
    e.preventDefault();
    gameState.toggleItemLock(itemId);
    updateDisplay();
}

/**
 * Handle custom input keypress (Enter to set)
 */
function handleCustomInputKeypress(e) {
    if (e.key === 'Enter') {
        const input = e.target;
        setQuantity(parseInt(input.value) || 1);
    }
}

/**
 * Open the shop modal
 */
export function openShopModal() {
    closeShopModal();

    // Reset to first category
    const categories = getAvailableCategories();
    currentCategory = categories.length > 0 ? categories[0].key : null;
    selectedQuantity = 1;
    quantityMode = 'fixed';
    isAwakeningView = false;

    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = createModalHTML();
    modalElement = modalContainer.firstElementChild;
    document.body.appendChild(modalElement);

    // Event listeners
    modalElement.addEventListener('click', handleModalClick);
    modalElement.addEventListener('contextmenu', handleShopContextMenu);

    const customInput = modalElement.querySelector('.shop-qty-input');
    if (customInput) {
        customInput.addEventListener('keypress', handleCustomInputKeypress);
    }

    // Close on Escape key
    document.addEventListener('keydown', handleEscapeKey);

    logger.debug('[SHOP] Modal opened');
}

/**
 * Close the shop modal
 */
export function closeShopModal() {
    if (modalElement) {
        modalElement.remove();
        modalElement = null;
        document.removeEventListener('keydown', handleEscapeKey);
        logger.debug('[SHOP] Modal closed');
    }
}

/**
 * Handle Escape key
 */
function handleEscapeKey(e) {
    if (e.key === 'Escape') {
        closeShopModal();
    }
}

// Expose to window for onclick handlers
window.shopModal = {
    open: openShopModal,
    close: closeShopModal,
    buyItem: purchaseItem,
    sellItem: sellItem,
};

export default {
    openShopModal,
    closeShopModal,
    purchaseItem,
    sellItem,
};
